import React from 'react';
import { X, Mic, Camera, Hash, Utensils, FileText, AlertTriangle, Palette, Loader2 } from 'lucide-react';
import { HealthEntry, PetProfile } from '../types';
import { MOODS, APPETITE_LEVELS, STOOL_SCORES, STOOL_COLORS } from '../constants';
import { useTranslation } from 'react-i18next';

interface LogFormProps {
  profile: PetProfile;
  newLog: {
    date: string;
    time: string;
    weight: string;
    mood: HealthEntry['mood'] | '';
    appetite: HealthEntry['appetite'] | '';
    stoolScore: number | '';
    stoolColor: string | '';
    notes: string;
  };
  setNewLog: (log: any) => void;
  isListening: boolean;
  handleVoiceInput: () => void;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploading: boolean;
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  isSaving: boolean;
  voiceProcessing: boolean;
}

const LogForm: React.FC<LogFormProps> = ({
  profile,
  newLog,
  setNewLog,
  isListening,
  handleVoiceInput,
  handleImageUpload,
  isUploading,
  onClose,
  onSave,
  isSaving,
  voiceProcessing
}) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-end backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md mx-auto p-6 rounded-t-[2.5rem] animate-slide-up relative shadow-2xl h-[80dvh] flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-black">{t('app:tracking.add_tracking')}</h3>
          <button onClick={onClose} className="text-gray-300"><X /></button>
        </div>
        
        <div className={`p-4 rounded-2xl mb-4 flex items-center justify-between ${profile?.type==='chat'?'bg-orange-50':'bg-indigo-50'}`}>
          <div className="flex-1">
            <p className={`font-bold text-sm ${profile?.type==='chat'?'text-orange-900':'text-indigo-900'}`}>{t('app:tracking.voice_input')}</p>
            <p className={`${profile?.type==='chat'?'text-orange-600':'text-indigo-600'} text-xs`}>{isListening ? t('app:tracking.listening') : voiceProcessing ? t('app:tracking.analyzing') : t('app:tracking.voice_example')}</p>
          </div>
          <button 
            onClick={handleVoiceInput}
            disabled={isListening || voiceProcessing}
            className={`p-3 rounded-full shadow-lg transition-all ${isListening ? 'bg-red-500 animate-pulse text-white' : (profile?.type==='chat'?'bg-orange-600':'bg-indigo-600') + ' text-white'}`}
          >
            {voiceProcessing ? <Loader2 className="animate-spin" size={20}/> : <Mic size={20}/>}
          </button>
        </div>

        <form onSubmit={onSave} className="space-y-6 flex-1 overflow-y-auto hide-scrollbar pb-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400">{t('app:tracking.date')}</label>
              <input type="date" required value={newLog.date} onChange={(e) => setNewLog(prev => ({ ...prev, date: e.target.value }))} className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400">{t('app:tracking.time')}</label>
              <input type="time" required value={newLog.time} onChange={(e) => setNewLog(prev => ({ ...prev, time: e.target.value }))} className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:tracking.weight_optional')}</label>
            <input type="number" step="0.1" value={newLog.weight} onChange={(e) => setNewLog(prev => ({ ...prev, weight: e.target.value }))} placeholder={t('app:tracking.weight_placeholder')} className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400">{t('app:tracking.mood')}</label>
              <select value={newLog.mood} onChange={(e) => setNewLog(prev => ({ ...prev, mood: e.target.value as any }))} className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none appearance-none bg-white">
                <option value="">{t('app:tracking.choose')}</option>
                {MOODS.map(m => <option key={m.value} value={m.value}>{t(`app:moods.${m.value}`)}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400">{t('app:tracking.appetite')}</label>
              <select value={newLog.appetite} onChange={(e) => setNewLog(prev => ({ ...prev, appetite: e.target.value as any }))} className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none appearance-none bg-white">
                <option value="">{t('app:tracking.choose')}</option>
                {APPETITE_LEVELS.map(a => <option key={a.value} value={a.value}>{t(`app:appetite.${a.value}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:tracking.stool_quality')}</label>
            <select value={newLog.stoolScore} onChange={(e) => setNewLog(prev => ({ ...prev, stoolScore: e.target.value ? Number(e.target.value) : '' }))} className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none appearance-none bg-white">
              <option value="">{t('app:tracking.choose')}</option>
              {STOOL_SCORES.map(s => <option key={s.value} value={s.value}>{t(`app:stool_scores.${s.value}`)}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:tracking.stool_color')}</label>
            <select value={newLog.stoolColor} onChange={(e) => setNewLog(prev => ({ ...prev, stoolColor: e.target.value }))} className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none appearance-none bg-white">
              <option value="">{t('app:tracking.choose')}</option>
              {STOOL_COLORS.map(c => <option key={c.value} value={c.value}>{t(`app:stool_colors.${c.value}`)}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:tracking.notes')}</label>
            <textarea value={newLog.notes} onChange={(e) => setNewLog(prev => ({ ...prev, notes: e.target.value }))} className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none h-24" placeholder={t('app:tracking.observation_placeholder')}></textarea>
          </div>
          <button disabled={isSaving} type="submit" className={`w-full ${profile?.type==='chat'?'bg-orange-600':'bg-indigo-600'} text-white py-5 rounded-[1.5rem] font-black shadow-xl`}>{t('common:buttons.save')}</button>
        </form>
      </div>
    </div>
  );
};

export default LogForm;
